import { useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { LOGO_URL, BUSINESS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data?.token) {
        onLogin(res.data.token);
      } else {
        setError("Login failed.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Login failed. Check the password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-gradient-to-br from-slate-50 via-white to-green-50/40" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt={BUSINESS.name} className="w-16 h-16 mx-auto rounded-2xl shadow-md" />
          <h1 className="font-display text-3xl font-black text-brand-navy mt-5 tracking-tight">Lead Admin</h1>
          <p className="text-sm text-slate-500 mt-1.5">Sign in to manage incoming quote requests.</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border border-slate-200 rounded-3xl shadow-xl p-7 md:p-8"
          data-testid="admin-login-form"
        >
          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
              Admin Password
            </span>
            <div className="relative">
              <Icons.Lock className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                autoFocus
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-11 pr-4 py-3.5 text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-red focus:bg-white transition-all"
                placeholder="••••••••••"
              />
            </div>
          </label>

          {error && (
            <div className="mt-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !password}
            data-testid="admin-login-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 font-bold rounded-full py-3.5 px-6 bg-brand-red hover:brightness-110 text-white transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <Icons.Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Icons.LogIn className="w-4 h-4" />
            )}
            {submitting ? "Signing in…" : "Sign In"}
          </button>

          <a
            href="/"
            data-testid="admin-back-home"
            className="mt-5 text-xs text-slate-500 hover:text-brand-red text-center block"
          >
            ← Back to website
          </a>
        </form>
      </div>
    </div>
  );
}
